import React, {
    forwardRef,
    memo,
    MouseEvent,
    ReactElement,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
    alpha,
    AppBar,
    Box,
    Button,
    Checkbox,
    colors,
    Dialog,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Slide,
    styled,
    SxProps,
    TextField,
    Theme,
    Toolbar,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/duotone-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import SkeletonExt from './SkeletonExt';
import AppSettings from '@/AppSettings';
import OverlayLoading from '../common/overlayLoading/OverlayLoading';

export interface IVirtualizedTableColumn<T extends object = any> {
    field: keyof T;
    headerName: string;
    tableHeaderSx?: SxProps<Theme>;
    tableCellSx?: SxProps<Theme>;
    sort?: (data: T) => any;
    getter?: (data: T) => any;
    cell?: (data: T, value: any) => ReactNode;
}

interface IVirtualizedTablSettings<T extends object = any> {
    columns: IVirtualizedTableColumn<T>[];
    filters: Record<string, any>;
    selectedColumns: string[];
    open?: boolean;
    setFilters: (filters: Record<string, any>) => void;
    setSelectedColumns: (selectedColumns: string[]) => void;
    onClose?: () => void;
}

interface IVirtualizedTable<T extends object = any> {
    data?: T[];
    columns: IVirtualizedTableColumn<T>[];
    loading?: boolean;
    settingsOpen?: boolean;
    onSettingsClose?: () => void;
    expandContent?: (data: T) => ReactNode;
}

enum SortDirection {
    Ascending = 1,
    Descending = 2,
}

interface ISortedField {
    field: string;
    direction: SortDirection;
}

const StyledTable = styled(Table)(({ theme }) => ({
    background: theme.palette.background.paper,
    backgroundImage:
        theme.palette.mode === 'dark'
            ? `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(
                    theme.palette.common.white,
                    0.05
                )})`
            : undefined,
    border:
        theme.palette.mode === 'dark'
            ? `solid 1px ${alpha(theme.palette.common.white, 0.12)}`
            : `solid 1px ${alpha(theme.palette.common.black, 0.12)}`,
}));

const StyledTableRow = styled(TableRow)(({ theme, ...rest }: any) => ({
    backgroundColor: rest.hasexpanded
        ? rest['data-index'] % 4 === 2 || rest['data-index'] % 4 === 3
            ? undefined
            : theme.palette.action.hover
        : rest['data-index'] % 2 === 1
        ? theme.palette.action.hover
        : undefined,
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& > .MuiDialog-container > .MuiPaper-root': {
        background: theme.palette.background.default,
    },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ? AppSettings.palette.backgroundAltDark : 'white',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#121212' : 'white',
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & { children: ReactElement },
    ref: Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ViurtalizedTableSettings = <T extends object = any>({
    columns,
    filters,
    selectedColumns,
    setFilters,
    setSelectedColumns,
    onClose,
    open,
}: IVirtualizedTablSettings<T>) => {
    const [settingsColumns, setSettingsColumns] = useState<string[]>([]);
    const [settingsFilters, setSettingsFilters] = useState<Record<string, any>>({});
    const theme = useTheme();

    useEffect(() => {
        setSettingsColumns([...selectedColumns]);
    }, [open, selectedColumns]);

    useEffect(() => {
        setSettingsFilters({ ...filters });
    }, [open, filters]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;

            setSettingsColumns(typeof value === 'string' ? value.split(',') : value);
        },
        [setSettingsColumns]
    );

    return (
        <StyledDialog fullScreen open={open ?? false} onClose={onClose} TransitionComponent={Transition}>
            <StyledAppBar position="sticky">
                <Toolbar>
                    <IconButton edge="start" onClick={onClose} aria-label="close">
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                    <Typography
                        color={theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main}
                        sx={{ ml: 2, flex: 1 }}
                        variant="h6"
                        component="div"
                    >
                        Table Settings
                    </Typography>
                </Toolbar>
            </StyledAppBar>

            <Typography variant="h4" color={theme.palette.secondary.main} sx={{ margin: 2, mb: 0 }}>
                Shown Columns
            </Typography>

            <StyledPaper sx={{ padding: 2, margin: 2 }}>
                <FormControl fullWidth size="small" margin="normal">
                    <InputLabel id="demo-multiple-checkbox-label">Columns</InputLabel>
                    <Select
                        labelId="demo-multiple-checkbox-label"
                        id="demo-multiple-checkbox"
                        multiple
                        value={settingsColumns}
                        onChange={handleChange}
                        input={<OutlinedInput label="Columns" />}
                        renderValue={(selected) =>
                            (selected as string[])
                                .map((x) => columns.find((y) => y.field === x)?.headerName)
                                .join(', ')
                        }
                    >
                        {columns.map((column) => {
                            const field = column.field.toString();
                            return (
                                <MenuItem key={field} value={field}>
                                    <Checkbox checked={settingsColumns.indexOf(field) > -1} />
                                    <ListItemText primary={column.headerName} />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </StyledPaper>

            <Typography color={theme.palette.secondary.main} variant="h4" sx={{ margin: 2, mb: 0 }}>
                Filters
            </Typography>

            <StyledPaper sx={{ padding: 2, margin: 2 }}>
                {columns.map((x) => {
                    const field = x.field.toString();
                    return (
                        <TextField
                            key={field}
                            value={settingsFilters[field] ?? ''}
                            onChange={(event) => setSettingsFilters((c) => ({ ...c, [field]: event.target.value }))}
                            margin="normal"
                            label={x.headerName}
                            fullWidth
                        />
                    );
                })}
            </StyledPaper>

            <Box sx={{ textAlign: 'right', mb: 2, mr: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120, mr: 2 }}
                    onClick={() => {
                        setFilters({ ...settingsFilters });
                        setSelectedColumns([...settingsColumns]);
                        onClose?.();
                    }}
                >
                    OK
                </Button>
                <Button variant="contained" color="primary" sx={{ minWidth: 120 }} onClick={onClose}>
                    Cancel
                </Button>
            </Box>
        </StyledDialog>
    );
};

interface IContext<T extends object = any> {
    expandContent?: (data: T) => ReactNode;
    expanded: Record<number, any>;
    columns: IVirtualizedTableColumn<T>[];
    setExpanded: (update: (expanded: Record<number, any>) => Record<number, any>) => void;
}

const TableComponent: TableComponents<IContext>['Table'] = (props) => (
    <StyledTable stickyHeader {...props} style={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
);
const TableHeadComponent: TableComponents<IContext>['TableHead'] = React.forwardRef((props, ref) => (
    <TableHead {...props} style={{ zIndex: 1, position: 'sticky', top: '64px' }} ref={ref} />
));
const TableRowComponent: TableComponents<IContext>['TableRow'] = React.forwardRef((props, ref) => (
    <StyledTableRow
        {...props}
        ref={ref as any}
        // @ts-ignore-next-line
        hasexpanded={props.context?.expandContent ? '1' : undefined}
    />
));

const TableBodyComponent: TableComponents<IContext>['TableBody'] = React.forwardRef((props, ref) => (
    <TableBody {...props} ref={ref} />
));

// Item contents are cached properly with React.memo
const InnerItem = memo(
    <T extends object = any>({ index, item, context }: { index: number; item: T; context: IContext<T> }) => {
        if (context.expandContent && index % 2 === 1) {
            if (!context.expanded[index - 1]) {
                return (
                    <>
                        <TableCell sx={{ padding: 0 }} colSpan={context.columns.length + 1}></TableCell>
                    </>
                );
            }

            return (
                <>
                    <TableCell></TableCell>
                    <TableCell sx={{ padding: 0 }} colSpan={context.columns.length}>
                        {context.expandContent(item)}
                    </TableCell>
                </>
            );
        }

        return (
            <>
                {context.expandContent && (
                    <TableCell sx={{ border: !context.expanded[index] ? 'none' : undefined }}>
                        <IconButton onClick={() => context.setExpanded((c) => ({ ...c, [index]: !c[index] }))}>
                            <FontAwesomeIcon icon={!context.expanded[index] ? faChevronDown : faChevronUp} />
                        </IconButton>
                    </TableCell>
                )}
                {context.columns.map((x) => {
                    const value = x.getter ? x.getter(item) : (item[x.field] as any);
                    const cell = x.cell ? x.cell(item, value) : value;

                    return (
                        <TableCell key={x.field.toString()} sx={{ ...x.tableCellSx, border: !context.expanded[index] ? 'none' : undefined }}>
                            {cell}
                        </TableCell>
                    );
                })}
            </>
        );
    }
);

// Item contents are cached properly with React.memo
const InnerSkeletonItem = memo(
    <T extends object = any>({ index, item, context }: { index: number; item: T; context: IContext<T> }) => {
        if (context.expandContent && index % 2 === 1) {
            if (!context.expanded[index - 1]) {
                return (
                    <>
                        <TableCell sx={{ padding: 0 }} colSpan={context.columns.length + 1}></TableCell>
                    </>
                );
            }

            return (
                <>
                    <TableCell></TableCell>
                    <TableCell sx={{ padding: 0 }} colSpan={context.columns.length}>
                        <SkeletonExt variant="rectangular" height={`50px`} width={`50%`} />
                    </TableCell>
                </>
            );
        }

        return (
            <>
                {context.expandContent && (
                    <TableCell sx={{ border: !context.expanded[index] ? 'none' : undefined }}>
                        <IconButton onClick={() => context.setExpanded((c) => ({ ...c, [index]: !c[index] }))}>
                            <FontAwesomeIcon icon={!context.expanded[index] ? faChevronDown : faChevronUp} />
                        </IconButton>
                    </TableCell>
                )}
                {context.columns.map((x) => {
                    return (
                        <TableCell key={x.field.toString()} sx={{ ...x.tableCellSx, border: !context.expanded[index] ? 'none' : undefined }}>
                            <SkeletonExt width={`80%`} />
                        </TableCell>
                    );
                })}
            </>
        );
    }
);

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = <T extends object = any>(index: number, item: T, context: IContext<T>) => {
    return <InnerItem index={index} item={item} context={context as any} />;
};

// The callback is executed often - don't inline complex components in here.
const virtuosoSkeletonItemContent = <T extends object = any>(index: number, item: T, context: IContext<T>) => {
    return <InnerSkeletonItem index={index} item={item} context={context as any} />;
};

const skeletonArray = Array(20).fill(0);

const VirtualizedTable = <T extends object = any>({
    data,
    columns,
    onSettingsClose,
    settingsOpen,
    expandContent,
    loading,
}: IVirtualizedTable<T>) => {
    const [sortedFields, setSortedFields] = useState<ISortedField[]>([]);
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [expanded, setExpanded] = useState<Record<number, any>>({});

    const availableColumns = useMemo(() => {
        if (!columns) return [];
        if (!data || data.length === 0) return [...columns];

        const c: IVirtualizedTableColumn<T>[] = [];
        for (let column of columns) {
            const hasValue = data.some((x) => {
                const aValue = column.getter ? column.getter(x) : (x[column.field] as any);
                return !!aValue;
            });
            if (hasValue) c.push(column);
        }
        return c;
    }, [data, columns]);

    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

    useEffect(() => {
        setSelectedColumns(availableColumns.map((x) => x.field.toString()));
    }, [availableColumns]);

    const columnsToUse = useMemo(() => {
        if (!columns) return [];
        const c: IVirtualizedTableColumn<T>[] = [];
        for (let column of columns) {
            if (selectedColumns.some((x) => x === column.field.toString())) {
                c.push(column);
            }
        }
        return c;
    }, [selectedColumns, columns]);

    const sortedData = useMemo(() => {
        if (!data || !columns) return data;

        const d = data
            .filter((x) => {
                for (let key of Object.keys(filters)) {
                    const column = columnsToUse.find((c) => c.field === (key as any));
                    if (!column) continue;
                    const filter = filters[key];
                    if (filter === undefined || filter === null) continue;
                    const value = column.getter ? column.getter(x) : (x[column.field] as any);
                    if (!value?.toString().includes(filter)) return false;
                }
                return true;
            })
            .sort((a, b) => {
                for (let sortedField of sortedFields) {
                    const column = columnsToUse.find((c) => c.field === (sortedField.field as any));
                    if (!column) continue;

                    const aValue = column.sort ? column.sort(a) : column.getter ? column.getter(a) : (a[column.field] as any);
                    const bValue = column.sort ? column.sort(b) : column.getter ? column.getter(b) : (b[column.field] as any);

                    if (aValue === bValue) continue;

                    if (aValue === null || aValue === undefined) {
                        return sortedField.direction === SortDirection.Ascending ? -1 : 1;
                    }

                    if (bValue === null || bValue === undefined) {
                        return sortedField.direction === SortDirection.Ascending ? 1 : -1;
                    }

                    if (aValue < bValue) {
                        return sortedField.direction === SortDirection.Ascending ? -1 : 1;
                    } else {
                        return sortedField.direction === SortDirection.Ascending ? 1 : -1;
                    }
                }
                return 1;
            });

        if (expandContent) {
            return d.reduce<Array<T>>((a, b) => {
                a.push(b);
                a.push(b);
                return a;
            }, []);
        }

        return d;
    }, [data, columnsToUse, sortedFields, filters, columns, expandContent]);

    const updateSort = useCallback((event: MouseEvent, field: string) => {
        setSortedFields((current) => {
            const found = current.find((x) => x.field === field);
            let sortedField: ISortedField | undefined = undefined;

            if (!found) {
                sortedField = { direction: SortDirection.Ascending, field };
            } else if (found.direction === SortDirection.Ascending) {
                sortedField = { direction: SortDirection.Descending, field };
            }

            const fields = event.ctrlKey ? current : [];

            if (!sortedField) {
                return fields.filter((x) => x !== found);
            }

            if (found && event.ctrlKey) {
                return fields.map((x) => (x === found ? sortedField! : x));
            }

            return [...fields, sortedField];
        });
    }, []);

    // allow expanded to stay when changing theme.
    useEffect(() => {
        setExpanded({});
    }, [data]);

    const context = useMemo(() => {
        return {
            loading,
            expandContent,
            setExpanded,
            expanded,
            columns: columnsToUse,
        } as IContext<T>;
    }, [expandContent, columnsToUse, expanded, loading]);

    const theme = useTheme();

    return (
        <>
            {loading ? (
                <OverlayLoading />
            ) : (
                <>
                    <ViurtalizedTableSettings
                        columns={availableColumns}
                        filters={filters}
                        selectedColumns={selectedColumns}
                        setSelectedColumns={setSelectedColumns}
                        setFilters={setFilters}
                        onClose={onSettingsClose}
                        open={settingsOpen}
                    />

                    <TableVirtuoso
                        context={context as any}
                        useWindowScroll
                        data={loading ? skeletonArray : (sortedData as any)}
                        components={{
                            Table: TableComponent,
                            TableHead: TableHeadComponent,
                            TableRow: TableRowComponent,
                            TableBody: TableBodyComponent,
                        }}
                        fixedHeaderContent={() => (
                            <>
                                <TableRow>
                                    {expandContent && (
                                        <TableCell
                                            sx={{
                                                width: 64,
                                                maxWidth: 64,
                                                backgroundColor: theme.palette.mode === 'dark' ? colors.grey[900] : 'white',
                                            }}
                                        ></TableCell>
                                    )}
                                    {columnsToUse.map((x) => {
                                        const sortedField = sortedFields.find((y) => y.field === (x.field as any));
                                        return (
                                            <TableCell key={x.field.toString()} sx={x.tableHeaderSx} onClick={(e) => updateSort(e, x.field.toString())}>
                                                <Box sx={{ display: 'flex', width: '100%', cursor: 'pointer' }}>
                                                    {x.headerName}
                                                    {sortedField && (
                                                        <Box sx={{ ml: 'auto' }}>
                                                            <FontAwesomeIcon icon={sortedField.direction === SortDirection.Descending ? faChevronDown : faChevronUp} />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </>
                        )}
                        itemContent={loading ? virtuosoSkeletonItemContent : virtuosoItemContent}
                    />
                </>
            )}
        </>
    );
};

export default VirtualizedTable;
